import { DayOfWeek, Instant, LocalDate, LocalDateTime } from "./JSJodaJSON";
import { PatientMedicationProfileWithDetails } from "./MedicationProfile";
import {
  NursingQuestionId,
  PatientDocumentScheduleId,
  PatientDocumentTypeId,
  PatientDocumentVersionId,
  PatientTaskId,
  ScheduledVisitId,
  VisitBroadcastId,
  VisitInstanceId,
} from "./ObjectIds";
import { PatientContractDiagnosisCode } from "./PatientContractDiagnosisCode";
import { TaskWhenToPerform } from "./PlanOfCare";
import { VitalExtraDetails, VitalMethodType, VitalUnitType } from "./Vital";

export type PatientDocumentTypeIdsMessage = PatientDocumentTypeId[];

export type PatientDocumentTypes = "GENERAL" | "PLAN_OF_CARE";

/** @TJS-type string */
export type DocumentItemQuestionType =
  | "radio"
  | "check"
  | "dropDown"
  | "yesNo"
  | "bigHeader"
  | "smallHeader"
  | "textShort"
  | "textLong"
  | "number"
  | "employeeSignature"
  | "patientSignature"
  | "customSignature"
  | "time"
  | "date"
  | "editor"
  | "chart"
  | "image"
  | "bodyDrawing"
  | "medicationProfile"
  | "medicationProfileItem"
  | "bloodPressure"
  | "icd" // @deprected but still here for older documents compatibility
  | "icdChart" // @deprecated but still here for older documents compatibility
  | "icdCodes"
  | "patientPhysician"
  | "POC";

export interface PatientDocumentContentItem {
  id: number;
  mainClass: string;
  title: string;
  itemType: DocumentItemQuestionType;
  label: string;
  columns?: null | PatientDocumentContentItem[][];
  possibleAnswers?: string[];
  /** Used only for document question type "POC" */
  POCItems?: PatientDocumentPOCItem[];
  isRequired?: boolean;
  parentId: null | number;
  showIfParentEquals: boolean | null;
  ifParentEquals?: string;
  showOtherOption?: boolean;
  html?: string;
  url?: string | null;
  days?: DayOfWeek[];
  frequencyPerWeek?: number;
  visitPerWeek?: number;
  notes?: string | null;
  parentType?: DocumentItemQuestionType | null;
  cms485QuestionId?: string;
  oca960questionId?: string;
  prefilledAnswer?: string | null;
  answerValues?: number[];
  valueGroup?: string | null;
  isInValueGroup?: boolean;
  copyFromOtherDocs?: boolean;
  allowUnsignedSignature?: boolean;
  hideNameFieldSignature?: boolean;
  useForMedication?: boolean;
  canCopyFromMedication?: boolean;
  useForDisciplinAndTreatment?: boolean;
  canCopyFromDisciplinAndTreatment?: boolean;
  isIcd?: boolean;
  nursingQuestionLinked?: NursingQuestionId;
  patientMedicationDetails?: PatientMedicationProfileWithDetails;
  vitalMeasuredAt?: LocalDateTime | null;
  vitalMethod?: VitalMethodType | null;
  vitalComments?: string | null;
  vitalUnit?: VitalUnitType | null;
  blockOnMobile?: boolean;
  isVital?: boolean;
}

export interface PatientDocumentPOCItem {
  name: string;
  isTitle: boolean;
}

export interface PatientDocumentAnswersToCopy {
  patientDocumentAnswersToCopy: PatientDocumentAnswerToCopy[];
}

export interface PatientDocumentAnswerToCopy {
  textToUse: string;
  dateUpdated: Instant;
}

export interface PatientMedicationProfileText {
  medicationProfileText: string | null;
}

export interface PatientDocumentContentVersion {
  id: PatientDocumentVersionId;
  createdAt: Instant;
  updatedAt: Instant | null;
  removedAt: Instant | null;
  content: PatientDocumentContentItem[];
  isPublished: boolean;
}

export interface PatientDocumentContentItemWithVersions {
  id: PatientDocumentTypeId;
  createdAt: Instant;
  updatedAt: Instant | null;
  removedAt: Instant | null;
  title: string;
  type: PatientDocumentTypes;
  versions: PatientDocumentContentVersion[];
}

export interface PatientDocumentTypesWithVersions {
  documents: PatientDocumentContentItemWithVersions[];
}

export interface PatientDocumentAnswerObject {
  questionId: number;
  chartRow: number | null;
  answer: Answer;
  answeredAt: Instant | null;
}

export interface PatientDocumentAnswers {
  answers: PatientDocumentAnswerObject[];
}

export interface PatientDocumentObject {
  id: PatientDocumentScheduleId;
  documentType: PatientDocumentTypeId;
  title: string;
  versionId: PatientDocumentVersionId;
  visitId: VisitBroadcastId;
  scheduledVisitId: ScheduledVisitId;
  submittedAt: Instant | null;
  content: PatientDocumentContentItem[];
  answers: PatientDocumentAnswerObject[];
  exportableQuestions: ExportableQuestions[];
}

export interface ExportableQuestions {
  documentType: PatientDocumentTypeId;
  documentTitle: string;
  questions: ExportableQuestion[];
}

export interface ExportableQuestion {
  questionId: number;
  questionLabel: string;
}

export interface PatientDocumentsPdfUrl {
  url: string;
}

export interface PatientDocumentTypeCreateParams {
  title: string;
  type: PatientDocumentTypes;
}

export interface PatientDocumentTypeEditParams {
  title?: string;
  isRemoved: boolean;
}

export interface PatientDocumentVersionParams {
  content: PatientDocumentContentItem[];
  isPublished: boolean;
}

export interface PatientDocumentCreateAnswer {
  answer: Answer;
  questionId: number;
  chartRow: number | null;
  createdAt?: Instant;
  vitalExtraDetails?: VitalExtraDetails;
}

export interface PatientDocumentCopyExported extends PatientDocumentCreateAnswer {
  exportedDocumentTypeId: PatientDocumentTypeId;
  exportedQuestionId: number;
}

type Answer =
  | null
  | string
  | string[]
  | PatientContractDiagnosisCode[]
  | POCItemsDocumentAnswer[]
  | PatientMedicationProfileWithDetails[];

export interface POCItemsDocumentAnswer {
  idx: number;
  attributes: TaskWhenToPerform;
  notes?: string | null;
}

export interface DocumentReplaceConstants {
  agencyName: string;
  caregiverName: string;
  patientName: string;
  agencyAddress: string;
  agencyPhone: string;
}

export interface PatientDocumentScanCreate {
  base64: string;
  // Provide the selected document id
  documentId: PatientDocumentTypeId | null;
  documentVersion: PatientDocumentVersionId | null;
  // OR provide the custom title for the document
  documentTitle: string | null;
}

export interface PatientDocumentTask {
  id: PatientDocumentScheduleId | null;
  documentType: PatientDocumentTypeId;
  title: string;
  taskId: PatientTaskId;
  createdAt: Instant;
  submittedAt: Instant | null;
  content: PatientDocumentContentItem[];
  answers: PatientDocumentAnswerObject[];
  pdfUrl: string | null;
  documentScanned: boolean;
  isScannable: boolean;
}

export interface PatientDocumentObjectPartial {
  id: PatientDocumentScheduleId | null;
  documentType: PatientDocumentTypeId;
  title: string;
  versionId: PatientDocumentVersionId;
  visitId: VisitBroadcastId;
  scheduledVisitId: VisitInstanceId;
  createdAt: Instant;
  submittedAt: Instant | null;
  content: PatientDocumentContentItem[];
  answers: PatientDocumentAnswerObject[];
  pdfUrl: string | null;
  documentScanned: boolean;
  isScannable: boolean;
}

export interface PatientDocumentSubmissionDate {
  submissionDate: LocalDate | null;
}

export interface PatientDocumentTaskInstanceSubmitRequest {
  date: LocalDate;
  regenerateNursingQuestionsOtherDocuments: boolean;
}

export enum CaregiverPatientDocumentType {
  Unanswered = "Unanswered",
  Answered = "Answered",
  FreeScan = "FreeScan",
  Scanned = "Scanned",
  ManualSubmission = "ManualSubmission",
}

type ManualSubmissionType = "MANUAL_SUBMISSION_STRING";
export const MANUAL_SUBMISSION_STRING: ManualSubmissionType = "MANUAL_SUBMISSION_STRING";

export interface CaregiverPatientDocumentResponse {
  documents: (
    | CaregiverPatientDocument.Unanswered
    | CaregiverPatientDocument.Answered
    | CaregiverPatientDocument.Scanned
    | CaregiverPatientDocument.ManualSubmission
  )[];
  freeScans: CaregiverPatientDocument.FreeScan[];
}

export declare namespace CaregiverPatientDocument {
  export interface Unanswered {
    type: CaregiverPatientDocumentType.Unanswered;
    visitInstanceId: VisitInstanceId;
    documentTypeId: PatientDocumentTypeId;
    title: string;
    versionId: PatientDocumentVersionId;
    content: PatientDocumentContentItem[];
    isScannable: boolean;
  }

  export interface Answered {
    type: CaregiverPatientDocumentType.Answered;
    id: PatientDocumentScheduleId;
    visitInstanceId: VisitInstanceId;
    documentTypeId: PatientDocumentTypeId;
    title: string;
    versionId: PatientDocumentVersionId;
    content: PatientDocumentContentItem[];
    answers: PatientDocumentAnswerObject[];
    submittedAt: Instant | null;
    isScannable: boolean;
  }

  export interface FreeScan {
    type: CaregiverPatientDocumentType.FreeScan;
    id: PatientDocumentScheduleId;
    visitInstanceId: VisitInstanceId;
    title: string;
    pdfUrl: string;
    submittedAt: Instant;
    isScannable: false;
  }

  export interface Scanned {
    type: CaregiverPatientDocumentType.Scanned;
    id: PatientDocumentScheduleId;
    visitInstanceId: VisitInstanceId;
    documentTypeId: PatientDocumentTypeId;
    title: string;
    pdfUrl: string;
    submittedAt: Instant;
    isScannable: true;
  }

  export interface ManualSubmission {
    type: CaregiverPatientDocumentType.ManualSubmission;
    id: PatientDocumentScheduleId;
    visitInstanceId: VisitInstanceId;
    documentTypeId: PatientDocumentTypeId;
    title: string;
    versionId: PatientDocumentVersionId;
    content: PatientDocumentContentItem[];
    pdfUrl: ManualSubmissionType;
    submittedAt: Instant;
    isScannable: false;
  }
}

export type CaregiverPatientDocument =
  | CaregiverPatientDocument.Unanswered
  | CaregiverPatientDocument.Answered
  | CaregiverPatientDocument.FreeScan
  | CaregiverPatientDocument.Scanned
  | CaregiverPatientDocument.ManualSubmission;

export interface CheckForOtherNursingQuestionsOnDocumentsResponse {
  documentsToBeRegenerated: string[];
}
